import React, { useState, useEffect } from 'react';
import { updateObstacle } from '../api/client';
import { T } from '../assets/theme';

const OBSTACLE_TYPES = ['Tower', 'Building', 'Crane', 'Wind Turbine', 'Chimney', 'Mast', 'Power Line', 'Bridge', 'Other'];

function decimalToDms(dd, isLat) {
    if (dd === null || dd === undefined || Number.isNaN(Number(dd))) return '';
    const value = Number(dd);
    const hemi = isLat ? (value >= 0 ? 'N' : 'S') : (value >= 0 ? 'E' : 'W');
    const absValue = Math.abs(value);
    const degrees = Math.floor(absValue);
    const minutesFull = (absValue - degrees) * 60;
    const minutes = Math.floor(minutesFull);
    const seconds = (minutesFull - minutes) * 60;
    return `${degrees}°${minutes.toString().padStart(2, '0')}'${seconds.toFixed(4).padStart(7, '0')}"${hemi}`;
}

function Btn({ children, onClick, variant = 'primary', small, disabled, compact, style: sx = {} }) {
    const variants = {
        primary: { bg: T.blue, color: T.white },
        danger: { bg: 'rgba(232,64,64,0.15)', color: '#e84040' },
        ghost: { bg: 'rgba(58,138,196,0.09)', color: T.blue },
    };
    const v = variants[variant] || variants.primary;
    return (
        <button onClick={onClick} disabled={disabled} style={{
            background: v.bg, color: v.color, border: `1px solid ${v.color}33`,
            borderRadius: 6, padding: compact ? (small ? '5px 10px' : '7px 14px') : (small ? '6px 13px' : '9px 18px'),
            fontSize: compact ? (small ? 10 : 11) : (small ? 12 : 14), fontWeight: 600,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1, transition: 'all 0.15s', ...sx,
        }}>{children}</button>
    );
}

// Shared obstacle-edit dialog — used both from the project obstacle list
// (ProjectPage) and from right-clicking an obstacle marker on any
// assessment map (ObstacleContextMenu). `compact` shrinks all text ~20%
// further — passed only from the assessment-map usage, where the modal
// floats over an already-dense map UI; ProjectPage keeps the larger size.
export default function EditObstacleModal({ obstacle, onClose, onUpdated, onRequestPickCoord, pickedCoord, hidden, compact = false }) {
    const [form, setForm] = useState({
        name: obstacle.name || '',
        obstacle_type: obstacle.obstacle_type || 'Tower',
        elevation_m: obstacle.elevation_m ?? '',
        h_tolerance: obstacle.h_tolerance ?? '',
        v_tolerance: obstacle.v_tolerance ?? '',
        description: obstacle.description || '',
        lat_dms: obstacle.lat_dms || (obstacle.latitude !== null && obstacle.latitude !== undefined ? decimalToDms(obstacle.latitude, true) : ''),
        lon_dms: obstacle.lon_dms || (obstacle.longitude !== null && obstacle.longitude !== undefined ? decimalToDms(obstacle.longitude, false) : ''),
    });
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    useEffect(() => {
        setForm({
            name: obstacle.name || '',
            obstacle_type: obstacle.obstacle_type || 'Tower',
            elevation_m: obstacle.elevation_m ?? '',
            h_tolerance: obstacle.h_tolerance ?? '',
            v_tolerance: obstacle.v_tolerance ?? '',
            description: obstacle.description || '',
            lat_dms: obstacle.lat_dms || (obstacle.latitude !== null && obstacle.latitude !== undefined ? decimalToDms(obstacle.latitude, true) : ''),
            lon_dms: obstacle.lon_dms || (obstacle.longitude !== null && obstacle.longitude !== undefined ? decimalToDms(obstacle.longitude, false) : ''),
        });
    }, [obstacle]);

    // Picked coordinate arrives from the parent after a map click — fill it
    // straight into the DMS fields, same spot the user would have typed into.
    useEffect(() => {
        if (!pickedCoord) return;
        set('lat_dms', decimalToDms(pickedCoord.lat, true));
        set('lon_dms', decimalToDms(pickedCoord.lon, false));
    }, [pickedCoord]);

    async function submit() {
        if (!form.name.trim()) {
            setErr('Name is required');
            return;
        }
        setLoading(true);
        setErr('');
        try {
            const payload = {
                name: form.name,
                obstacle_type: form.obstacle_type,
                description: form.description || null,
                elevation_m: form.elevation_m !== '' ? Number(form.elevation_m) : null,
                h_tolerance: form.h_tolerance !== '' ? Number(form.h_tolerance) : null,
                v_tolerance: form.v_tolerance !== '' ? Number(form.v_tolerance) : null,
                lat_dms: form.lat_dms || null,
                lon_dms: form.lon_dms || null,
                project_id: obstacle.project_id,
                aerodrome: obstacle.aerodrome || 'FAOR',
                runway: obstacle.runway || null,
            };
            const { data } = await updateObstacle(obstacle.id, payload);
            onUpdated(data);
            onClose();
        } catch (err) {
            setErr(err.response?.data?.detail || 'Failed to update obstacle.');
        } finally {
            setLoading(false);
        }
    }

    const titleSize = compact ? 15 : 19;
    const closeSize = compact ? 18 : 22;
    const labelSize = compact ? 11 : 14;
    const inputSize = compact ? 13 : 16;
    const errSize = compact ? 11 : 14;
    const inputPad = compact ? '8px 10px' : '10px 12px';
    const inputSx = { background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 6, padding: inputPad, color: T.white, fontSize: inputSize, width: '100%' };
    const labelSx = { fontSize: labelSize, color: T.muted, textTransform: 'uppercase', marginBottom: 4 };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: hidden ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
            <div style={{ background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 12, padding: compact ? 18 : 24, width: compact ? 520 : 640, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                    <div style={{ fontSize: titleSize, fontWeight: 800 }}>Edit Obstacle</div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.muted, fontSize: closeSize, cursor: 'pointer' }}>✕</button>
                </div>
                <div style={{ display: 'grid', gap: 12 }}>
                    <div>
                        <div style={labelSx}>Obstacle Name</div>
                        <input value={form.name} onChange={e => set('name', e.target.value)} style={inputSx} />
                    </div>
                    <div>
                        <div style={labelSx}>Type</div>
                        <select value={form.obstacle_type} onChange={e => set('obstacle_type', e.target.value)} style={inputSx}>
                            {OBSTACLE_TYPES.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <div>
                            <div style={labelSx}>Elevation (m AMSL)</div>
                            <input type="number" value={form.elevation_m} onChange={e => set('elevation_m', e.target.value)} style={inputSx} />
                        </div>
                        <div>
                            <div style={labelSx}>H Tolerance (m)</div>
                            <input type="number" value={form.h_tolerance} onChange={e => set('h_tolerance', e.target.value)} style={inputSx} />
                        </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <div>
                            <div style={labelSx}>V Tolerance (m)</div>
                            <input type="number" value={form.v_tolerance} onChange={e => set('v_tolerance', e.target.value)} style={inputSx} />
                        </div>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                                <div style={{ fontSize: labelSize, color: T.muted, textTransform: 'uppercase' }}>Coordinates</div>
                                <button
                                    onClick={onRequestPickCoord}
                                    style={{ background: 'none', border: 'none', color: T.blue, fontSize: labelSize, cursor: 'pointer', fontWeight: 600 }}
                                >
                                    📍 Pick on Map
                                </button>
                            </div>
                            <div style={{ display: 'grid', gap: 8 }}>
                                <input value={form.lat_dms} onChange={e => set('lat_dms', e.target.value)} placeholder="Lat DMS or decimal" style={inputSx} />
                                <input value={form.lon_dms} onChange={e => set('lon_dms', e.target.value)} placeholder="Lon DMS or decimal" style={inputSx} />
                            </div>
                        </div>
                    </div>
                    <div>
                        <div style={labelSx}>Description</div>
                        <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3} style={{ ...inputSx, resize: 'vertical' }} />
                    </div>
                    {err && <div style={{ color: '#e84040', fontSize: errSize }}>{err}</div>}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Btn variant="ghost" onClick={onClose} compact={compact}>Cancel</Btn>
                        <Btn onClick={submit} disabled={loading} compact={compact}>{loading ? 'Saving…' : 'Save Changes'}</Btn>
                    </div>
                </div>
            </div>
        </div>
    );
}
